import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { RemakeApplyOverlayHeader } from './RemakeApplyOverlayHeader'
import { RemakeApplyProductList } from './RemakeApplyProductList'
import { RemakeApplyTabBar } from './RemakeApplyTabBar'
import { RemakeApplyEmptyMid } from './RemakeApplyEmptyMid'
import { RemakeApplyContentMode } from './RemakeApplyContentMode'
import { RemakeApplyMediaMode } from './RemakeApplyMediaMode'
import { RemakeApplyRemakeSidebar } from './RemakeApplyRemakeSidebar'
import type { ProductSearchMatch } from '@/lib/api/products-search'

interface RemakeJury {
  agent_style: string
  title: string
  bullets: string[]
  description: string
  backend_keywords?: { search_terms?: string }
}

interface RemakeResultShape {
  variants: RemakeJury[]
  jury_index: number
}

// PR 2 Task #11: typed loosely so callers can pass an arbitrary report shape.
// ContentMode / RemakeSidebar only read `variants` + `jury_index`.
export type RemakeApplyOverlayResult = RemakeResultShape | null

export interface RemakeApplyOverlayProps {
  asin: string
  versionNumber: number
  completedAt: string
  identifiers: { sku: string | null; gtin: string | null; asin: string | null }
  remakeResult: RemakeApplyOverlayResult
  remakeId: string | null
  onClose: () => void
}

const DIALOG_TITLE_ID = 'remake-apply-overlay-title'

export function RemakeApplyOverlay({
  asin,
  versionNumber,
  completedAt,
  identifiers,
  remakeResult,
  remakeId,
  onClose,
}: RemakeApplyOverlayProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [contentDirty, setContentDirty] = useState(false)
  const [selected, setSelected] = useState<ProductSearchMatch | null>(null)
  const [activeDrag, setActiveDrag] = useState<{ assetUrl: string } | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: 'content' | 'media' = searchParams.get('applyTab') === 'media' ? 'media' : 'content'
  const setTab = (next: 'content' | 'media') => {
    const sp = new URLSearchParams(searchParams)
    sp.set('applyTab', next)
    setSearchParams(sp, { replace: true })
  }

  const handleCloseAndCleanup = () => {
    const sp = new URLSearchParams(searchParams)
    sp.delete('applyTab')
    setSearchParams(sp, { replace: true })
    onClose()
  }

  const handleOpenProduct = () => {
    if (!selected) return
    if (contentDirty && !window.confirm(t('remakeApply.unsavedConfirm'))) return
    navigate(`/v2/products/${selected.id}`)
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseAndCleanup()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, searchParams])

  // Body scroll-lock while overlay is mounted
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [])

  const handleDragStart = (event: DragStartEvent) => {
    const d = event.active.data.current as { assetUrl?: string } | undefined
    if (d?.assetUrl) setActiveDrag({ assetUrl: d.assetUrl })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null)
    const dropFn = event.over?.data.current?.onDrop as
      | ((slot: number, payload: { generationId: string; assetUrl: string }) => void)
      | undefined
    const slot = event.over?.data.current?.slot as number | undefined
    const payload = event.active.data.current as
      | { generationId: string; assetUrl: string }
      | undefined
    if (dropFn && typeof slot === 'number' && payload) {
      dropFn(slot, payload)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={DIALOG_TITLE_ID}
      className="fixed inset-0 z-50 flex flex-col bg-neutral-50 dark:bg-neutral-950"
    >
      <RemakeApplyOverlayHeader
        asin={asin}
        versionNumber={versionNumber}
        completedAt={completedAt}
        onClose={handleCloseAndCleanup}
        titleId={DIALOG_TITLE_ID}
      />
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDrag(null)}
      >
        <div className="flex-1 grid grid-cols-[280px_1fr_320px] overflow-hidden">
          <RemakeApplyProductList
            identifiers={identifiers}
            selectedProductId={selected?.id ?? null}
            onSelect={setSelected}
          />
          {selected ? (
            <div className="flex flex-col overflow-hidden">
              <RemakeApplyTabBar
                tab={tab}
                onTabChange={setTab}
                onOpenProduct={handleOpenProduct}
              />
              {/* contentDirty bleibt absichtlich true, wenn von Content zu Media gewechselt wird: die Inhaltsänderungen sind real ungespeichert und gehen bei Navigation verloren — Warnung soll daher auch aus dem Media-Tab erscheinen. Nicht "fixen". */}
              {tab === 'content' ? (
                <RemakeApplyContentMode
                  productId={selected.id}
                  workspaceIntegrationId={selected.workspace_integration_id}
                  remakeResult={remakeResult}
                  onDirtyChange={setContentDirty}
                />
              ) : (
                <RemakeApplyMediaMode
                  productId={selected.id}
                  workspaceIntegrationId={selected.workspace_integration_id}
                  remakeId={remakeId}
                />
              )}
            </div>
          ) : (
            <RemakeApplyEmptyMid />
          )}
          <RemakeApplyRemakeSidebar remakeResult={remakeResult} remakeId={remakeId} />
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDrag ? (
            <img
              src={activeDrag.assetUrl}
              alt=""
              className="w-24 h-24 object-cover rounded-lg shadow-2xl ring-2 ring-orange-500 pointer-events-none"
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
